/**
 * OutboundGuardPanel — estado del "Outbound Guard" (preview/test) y registro de
 * comunicaciones bloqueadas/redirigidas (WhatsApp, email, Telegram, FacturaDirecta).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldAlert, RefreshCw, Loader2 } from "lucide-react";

const CH = { whatsapp: "WhatsApp", email: "Email", telegram: "Telegram", facturadirecta: "FacturaDirecta" };
const ACT = { block: ["Bloqueado", "bg-rose-500/15 text-rose-200 border-rose-500/30"],
  redirect: ["Redirigido", "bg-amber-500/15 text-amber-200 border-amber-500/30"],
  allow_allowlist: ["Permitido (interno)", "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"] };

export default function OutboundGuardPanel() {
  const [st, setSt] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/admin/outbound-guard"), api.get("/admin/outbound-guard/log?limit=100")]);
      setSt(a.data); setLog(b.data.items || []);
    } catch { /* toast ya lo hace el interceptor */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!st && loading) return null;
  const active = st?.active;
  return (
    <section className={`rounded-xl border p-4 space-y-3 ${active ? "border-emerald-500/30 bg-emerald-500/5" : "border-zinc-700 bg-zinc-900/40"}`}
      data-testid="outbound-guard-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {active ? <ShieldCheck className="w-5 h-5 text-emerald-400" /> : <ShieldAlert className="w-5 h-5 text-zinc-400" />}
          <div>
            <div className="font-semibold text-sm" data-testid="outbound-guard-title">
              Outbound Guard · {active ? "ACTIVO (preview/test)" : "Producción · envíos reales activos"}
            </div>
            <div className="text-[11px] text-zinc-400">
              {active
                ? `Entorno ${st.app_env} · política ${st.policy === "redirect" ? "redirigir al interno" : "bloquear"} · FD escrituras ${st.facturadirecta_writes === "blocked" ? "bloqueadas" : "permitidas"}`
                : "APP_ENV=production: WhatsApp, email, Telegram y FacturaDirecta llegan a los destinatarios reales."}
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="outbound-guard-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
      </div>
      {active && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs" data-testid="outbound-guard-allowlist">
          <Info k="WhatsApp permitido" v={st.test_whatsapp_phone || "— (todo bloqueado)"} />
          <Info k="Emails permitidos" v={(st.test_email_allowlist || []).join(", ") || "— (todo bloqueado)"} />
          <Info k="Telegram permitido" v={st.test_telegram_chat_id || "— (todo bloqueado)"} />
        </div>
      )}
      <div className="text-xs text-zinc-400">Eventos registrados: <b className="text-zinc-200" data-testid="outbound-guard-total">{st?.log_total ?? 0}</b></div>
      {log.length > 0 && (
        <div className="max-h-72 overflow-auto rounded-lg border border-zinc-800" data-testid="outbound-guard-log">
          <table className="w-full text-[11px]">
            <thead className="bg-zinc-900/60 text-zinc-400 sticky top-0">
              <tr><th className="text-left p-2">Cuándo</th><th className="text-left p-2">Canal</th><th className="text-left p-2">Acción</th><th className="text-left p-2">Destino (redactado)</th><th className="text-left p-2">Detalle</th></tr>
            </thead>
            <tbody>
              {log.map((it, i) => {
                const [lbl, cls] = ACT[it.action] || [it.action, "bg-zinc-800 text-zinc-300 border-zinc-700"];
                return (
                  <tr key={i} className="border-t border-zinc-800/70" data-testid={`outbound-guard-row-${i}`}>
                    <td className="p-2 text-zinc-400 whitespace-nowrap">{(it.ts || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-2">{CH[it.channel] || it.channel}</td>
                    <td className="p-2"><span className={`px-1.5 py-0.5 rounded border ${cls}`}>{lbl}</span></td>
                    <td className="p-2 font-mono">{it.recipient_redacted || "—"}</td>
                    <td className="p-2 text-zinc-400">{it.meta?.template || it.meta?.subject || it.meta?.tg_method || `${it.method} ${it.path}`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Info({ k, v }) {
  return (
    <div className="rounded-lg bg-zinc-900/50 border border-zinc-800 p-2">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
      <div className="text-zinc-200 font-mono break-all">{v}</div>
    </div>
  );
}
